import type { NodeSearchResult } from 'app/api/search/[keywords]/route';
import { useState } from 'react';
import useSWR from 'swr';
import { SEARCH_POSTS_PER_PAGE } from 'utils/shared';

const fetcher = async (...args: Parameters<typeof fetch>): Promise<NodeSearchResult> => {
  const response = await fetch(...args);
  if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
  return response.json();
};

type Options = {
  itemsPerPage?: number;
  /** Called when the search for `input` resolves with no matches. */
  onNoResults?: () => void;
};

export default function usePostsSearch(
  input: string,
  page: number,
  { itemsPerPage = SEARCH_POSTS_PER_PAGE, onNoResults }: Options = {},
) {
  const [searchTotal, setSearchTotal] = useState<{ input: string; total: number } | null>(null);
  const start = (Math.max(1, page) - 1) * itemsPerPage;
  const skipSearch = input.trim().length < 3;
  const onSuccess = (result: NodeSearchResult) => {
    if (result?.total === 0) onNoResults?.();
  };
  const { data, error } = useSWR<NodeSearchResult>(
    skipSearch
      ? null
      : `${process.env.NEXT_PUBLIC_BASE_PATH}/api/search/${encodeURIComponent(
          input,
        )}/?limit=${itemsPerPage}&start=${start}`,
    fetcher,
    { onSuccess },
  );

  const isLoading = !skipSearch && !data && !error;
  const responseTotal = data?.total;

  if (
    !skipSearch &&
    responseTotal != null &&
    (searchTotal?.input !== input || searchTotal.total !== responseTotal)
  ) {
    setSearchTotal({ input, total: responseTotal });
  }
  const retainedTotal = searchTotal?.input === input ? searchTotal.total : null;
  const total = responseTotal ?? retainedTotal;

  return {
    posts: data?.posts ?? [],
    categories: data?.categories ?? [],
    isLoading,
    total: skipSearch ? null : total,
  };
}
